import { InfoIcon } from '@/assets/icons/info-icons/info';
import { BannerDivider, Description, IconWrapper, StatusBannerWrapper, StatusWrapper } from '@/components/basicComponents/banner';
import { updateWizardSteps, WizardProgressBar } from '@/components/basicComponents/wizard/progressBar';
import FooterModal from '@/components/settings-page/importData/footer';
import { useResponsive } from '@/hooks/use-responsive';
import { useGoBack } from '@/hooks/useGoBack';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { advisoryFirmsStepRadioOptions, ManagerSpotlightWizardStepWrapper } from '..';
import { DEFAULT_RESTRICTIONS_ERROR } from '../../modify/constants';
import { RadioWithDynamicSelects } from '../../modify/radioWithDynamicSelects';
import { IListData } from '../../modify/types';

interface AdvisoryFirmsStepProps {
  managerSpotlightWizardSteps: { name: string; status: string }[];
  selectedAdvisoryFirmsStepRadioOption: string;
  advisoryFirms: IListData;
  setAdvisoryFirms: Dispatch<SetStateAction<IListData>>;
  handleTenantIds: (chosenAdvisoryFirms: string[], selectedAdvisoryFirmsStepRadioOption: string) => void;
  setSelectedAdvisoryFirmsStepRadioOption: Dispatch<SetStateAction<string>>;
  handleManagerSpotlightWizardSteps: (steps: { name: string; status: string }[]) => void;
}

export const AdvisoryFirmsStep = ({
  managerSpotlightWizardSteps,
  selectedAdvisoryFirmsStepRadioOption,
  advisoryFirms,
  setAdvisoryFirms,
  handleTenantIds,
  setSelectedAdvisoryFirmsStepRadioOption,
  handleManagerSpotlightWizardSteps
}: AdvisoryFirmsStepProps) => {
  const theme = useTheme();
  const goBack = useGoBack();
  const { isMobile } = useResponsive();

  const [errorMessage, setErrorMessage] = useState<string>(null);

  useEffect(() => {
    if (errorMessage && advisoryFirms.chosen.length && !advisoryFirms.chosen.includes('')) {
      setErrorMessage(null);
    }

    setAdvisoryFirms((prevAdvisoryFirms) => {
      return {
        ...prevAdvisoryFirms,
        list: prevAdvisoryFirms.list
          .filter((advisoryFirm) => !advisoryFirms.chosen.includes(advisoryFirm))
          .sort((a: string, b: string) => a.localeCompare(b))
      };
    });
  }, [advisoryFirms.chosen]);

  useEffect(() => {
    if (errorMessage && selectedAdvisoryFirmsStepRadioOption === advisoryFirmsStepRadioOptions[0]) {
      setErrorMessage(null);
    }
  }, [selectedAdvisoryFirmsStepRadioOption]);

  const saveAndContinue = () => {
    if (
      selectedAdvisoryFirmsStepRadioOption === advisoryFirmsStepRadioOptions[1] &&
      (!advisoryFirms.chosen.length || advisoryFirms.chosen.includes(''))
    ) {
      setErrorMessage(DEFAULT_RESTRICTIONS_ERROR);
      return;
    }

    handleTenantIds(advisoryFirms.chosen, selectedAdvisoryFirmsStepRadioOption);
    updateWizardSteps('next', managerSpotlightWizardSteps, handleManagerSpotlightWizardSteps);
  };

  return (
    <>
      <ManagerSpotlightWizardStepWrapper isMobile={isMobile}>
        <WizardProgressBar steps={managerSpotlightWizardSteps} />
        <AdvisoryFirmsStepWrapper>
          <StatusBannerWrapper bgColor={theme.layer[12]} withBorderRadius>
            <IconWrapper>
              <InfoIcon width={26} height={26} fill={theme.font.base} />
            </IconWrapper>
            <BannerDivider dividerColor={theme.font.disabled} />
            <StatusWrapper>
              <Description color={theme.font.base}>
                Promote current and upcoming opportunties to advisors. Start by selecting which advisors should receive the email notification.
              </Description>
            </StatusWrapper>
          </StatusBannerWrapper>
          <RadioWithDynamicSelects
            radioOptions={advisoryFirmsStepRadioOptions}
            selectedRadioOption={selectedAdvisoryFirmsStepRadioOption}
            radioLabel="Select the advisory firms you would like to send this spotlight to:"
            radioError={errorMessage}
            dynamicItems={advisoryFirms}
            setDynamicItems={setAdvisoryFirms}
            handleRadioChange={setSelectedAdvisoryFirmsStepRadioOption}
          />
        </AdvisoryFirmsStepWrapper>
      </ManagerSpotlightWizardStepWrapper>
      <FooterModal
        startOver={() => goBack({ fallBack: '/dealPipeline' })}
        startOverButtonName="Back"
        confirmButton={{
          name: 'Save And Continue',
          onClick: saveAndContinue,
          disabled: !!errorMessage
        }}
      />
    </>
  );
};

const AdvisoryFirmsStepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
